
// Action: file_editor create /app/frontend/src/components/Contact.js --file-text 
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Since this is frontend only, we'll just show a success message
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 9599173367', ''],
      link: 'tel:+919599173367'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['Mishivam22076@gmail.com'],
      link: 'mailto:Mishivam22076@gmail.com'
    },
    {
      icon: MapPin,
      title: 'Location',
      details: ['J-80, Groud Floor, Street No. 8, 4th Pusta, Kartar Nagar, Delhi-53 '],
      link: 'https://maps.app.goo.gl/v2Ab29kETvC6ekJo8'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Daily: 9:00 AM - 9:00 PM'],
      link: null
    }
  ];

  return (
    <section data-testid="contact-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-manrope text-sm tracking-widest uppercase text-[#D4AF37] mb-4">
            Get In Touch
          </p>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
            Let's Design Your <span className="italic">Dream Space</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-[#666666] max-w-2xl mx-auto">
            Have a project in mind? We'd love to hear from you. Send us a message 
            and we'll respond as soon as possible.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:col-start-2 lg:col-span-1 space-y-6"
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`contact-info-${info.title.toLowerCase()}`}
                className="bg-[#F9F8F6] p-6 rounded-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <info.icon size={24} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-playfair text-xl font-semibold text-[#1A1A1A] mb-2">
                      {info.title}
                    </h3>
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="font-manrope text-sm text-[#666666]">
                        {info.link && idx === 0 ? (
                          <a
                            href={info.link}
                            className="hover:text-[#D4AF37] transition-colors duration-300"
                          >
                            {detail}
                          </a>
                        ) : (
                          detail
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contact Form
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="bg-[#F9F8F6] p-8 md:p-12 rounded-sm">
              <h3 className="font-playfair text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-6">
                Send Us a Message
              </h3>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="form-success-message"
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-sm mb-6"
                >
                  Thank you for your message! We'll get back to you soon.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2"
                    >
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="contact-form-name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-sm font-manrope focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="contact-form-email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-sm font-manrope focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2"
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="contact-form-phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-sm font-manrope focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2"
                    >
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      data-testid="contact-form-subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-sm font-manrope focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                      placeholder="Interior Design Inquiry"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2"
                  >
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-form-message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-sm font-manrope focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  className="group w-full md:w-auto bg-[#D4AF37] text-[#1A1A1A] px-10 py-4 rounded-full font-manrope font-medium hover:bg-[#1A1A1A] hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Send size={20} />
                  <span>Send Message</span>
                </button>
              </form>
            </div>
          </motion.div> */}
        </div>
      </div>
    </section>
  );
};

export default Contact;
// Observation: Create successful: /app/frontend/src/components/Contact.js